import React, { useState } from 'react'
import styled from 'styled-components'
import { up } from 'styled-breakpoints'
import { Link, useLocation, useRouteMatch } from 'react-router-dom'

import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

import { useDialog } from '../../dialog/hook'
import { useAuth } from '../../../../data/hooks/auth'
import { Profile } from '../../../../data/profile/data-types'

import Stats, { Statistic } from '../../stats'
import Section from './section'
import Tabs from '../../tabs'
import LibraryImports from './library-imports'
import { changeFollowStatus } from '../../../../data/profile/folllow'
import ProfileReviews from './profile-reviews'
import GoodreadsImportDialog from '../goodreads-import'
import Followers from './followers'
import Following from './following'


type ProfileInfoProps = {
  profile: Profile
}

const ProfileInfo: React.FunctionComponent<ProfileInfoProps> = ({ profile }) => {
    const { user, token } = useAuth()
    const { t } = useTranslation("profile")
    const { t: commonT } = useTranslation("common")
    
    const { closeDialog, setComponent } = useDialog()
    
    // Track current tab
    const location = useLocation()
    const params = new URLSearchParams(location.search)
    const defaultTab = params.get("tab") || "clubs"
    const [activeTab, setTab] = useState(defaultTab)

    // Track following
    const isMy = user?.public_id === profile.public_id
    const [following, setFollowing] = useState(profile.followed_by_me)

    const stats = getStats(profile, t)
    const captions = getCaptions(t, isMy)

    return (
        <Container>
            <ProfileCard>
                <Image src={profile.picture || require('./images/default.svg')} />
                <About>
                    <Name>{profile.first_name + ' ' + profile.last_name}</Name>
                    <Bio>{profile.bio?.split('\n').map(el => <React.Fragment key={el}>{el} <br /></React.Fragment>)}</Bio>
                </About>
                <Buttons>
                    <Button 
                        to={`/library?public_id=${profile.public_id}`} 
                        active={true}
                        onClick={() => closeDialog()}
                    >
                        {t("library")}
                    </Button>
                    { !isMy && token != null && 
                        <Button 
                            to={""} 
                            active={!following}
                            onClick={async (ev) => {
                                ev.preventDefault()
                                setFollowing(!following)
                                await changeFollowStatus(profile.public_id, profile.followed_by_me || false, token.access)
                            }}
                        >
                            { following ? commonT('actions.unfollowUser') : commonT('actions.followUser') }
                        </Button>
                    }
                    { isMy && 
                        <Button
                            to=""
                            active={true}
                            onClick={ev => {
                                ev.preventDefault()
                                setComponent(<GoodreadsImportDialog />)
                            }}
                        >
                            Goodreads import
                        </Button>
                    }
                </Buttons>
                <ProfileStats 
                    stats={stats} 
                    justify="center" 
                    valueTextAlign='center' 
                    autoPadding 
                    allSeparators={window.innerWidth > 1024} 
                    separatorColor="#D5DDE7"
                />
            </ProfileCard>
            <Extra>
                <Tabs value={activeTab} captions={captions} onChange={s => setTab(s as string)} />
                { 
                    activeTab === "clubs" && 
                    <ClubsSection>
                        <Section title={t("last_clubs_joined")} clubs={profile.clubs.last_joined} />
                        <Section title={t("last_clubs_commented")} clubs={profile.clubs.last_commented} />
                    </ClubsSection> 
                }
                { 
                    activeTab === "imports" &&
                    <LibraryImports />
                }
                {
                    activeTab === "reviews" &&
                    <ProfileReviews publicId={profile.public_id}/>
                }
                { activeTab === "followers" && <Followers targetPublicId={isMy ? null : profile.public_id}/> }
                { activeTab === "following" && <Following targetPublicId={isMy ? null : profile.public_id}/> }
            </Extra>
        </Container>
    )
}

const getCaptions = (t: TFunction, isMy: boolean): { value: string, title: string }[] => {
    const captions = [
        {
            title: t('tabs.clubs'),
            value: 'clubs',
        },
        {
            title: t('tabs.reviews'),
            value: 'reviews',
        },
        {
            title: t('tabs.followers'),
            value: 'followers',
        },
        {
            title: t('tabs.following'),
            value: 'following',
        }
    ]

    if (isMy) {
        captions.push({
            title: 'Imports',
            value: 'imports',
        })
    }

    return captions
}


const getStats = (profile: Profile | null, t: TFunction): Statistic[] => {
    if (profile == null) {
        return []
    }

    const source = profile.stats

    return [
        {
            label: t("stats.followers"),
            type: 'number',
            value: source.followers,
        },
        {
            label: t("stats.following"),
            type: 'number',
            value: source.following,
        },
        {
            label: t("stats.joined"),
            type: 'date',
            value: new Date(profile.registered),
        },
        {
            label: t("stats.messages"),
            type: 'number',
            value: source.messages,
        },
        {
            label: t("stats.pages"),
            type: 'number',
            value: source.pages_read,
        },
        {
            label: t("stats.books"),
            type: 'number',
            value: source.books_added,
        },
    ]
}


const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 48px;

    justify-items: start;
    align-items: start;

    min-height: 100%;
    width: 100%;
`

const ProfileCard = styled.div`
    display: grid;
    grid-gap: 16px;
    grid-template-columns: 1fr;
    justify-items: center;

    width: 100%;
    padding: 24px 16px;

    border-radius: 10px;
    background: ${p => p.theme.colors.bg};

    ${up('tablet')} {
        justify-items: start;
        grid-template-columns: auto 1fr;
        grid-gap: 32px;

        padding: 64px 32px; 
    }

    ${up('desktop')} {
        grid-template-columns: auto 1fr auto;
        grid-gap: 40px;

        padding: 80px 64px; 
    }
`

const Image = styled.img`
    width: 128px;
    height: 128px;
    border-radius: 50%;
`

const About = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
    grid-row-gap: 8px;

    ${up('tablet')} {
        grid-row-gap: 16px;
    }
`

const Name = styled.div`
    text-align: center;
    font-size: 24px;
    font-weight: 600;
    line-height: 28px;

    ${up('tablet')} {
        text-align: left;
    }
`

const Bio = styled.div`
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;

    ${up('tablet')} {
        text-align: left;
    }
`

const Buttons = styled.div`
    display: grid;
    grid-gap: 16px;

    width: 100%;
    height: fit-content;

    ${up('tablet')} {
        grid-column: 1 / span 2;
    }

    ${up('desktop')} {
        grid-column: any;
    }
`

const Button = styled(Link)<{ active?: boolean }>`
    display: grid;
    align-items: center;
    justify-content: center;

    width: 100%;
    height: fit-content;
    padding: 10px 32px;

    font-size: 16px;
    font-weight: 600;
    line-height: 24px;

    background: ${p => p.active ? p.theme.colors.accent : p.theme.colors.bg};

    border-color: ${p => p.active ? p.theme.colors.bg : p.theme.colors.accent};
    border-width: 1px;
    border-style: ${p => p.active ? 'none' : 'solid' };
    border-radius: 10px;
    cursor: pointer;

    &, &:active, &:visited {
        text-decoration: none;
        outline: none;
        color: ${p => p.active ? p.theme.colors.bg : p.theme.colors.accent}
    }
`

const ProfileStats = styled(Stats)`
    ${up('tablet')} {
        grid-column: 1 / span 2;
    }

    ${up('desktop')} {
        grid-template-columns: 1fr 1px 1fr 1px 1fr 1px 1fr 1px 1fr 1px 1fr; // 6 + 5 separators
        grid-column: 1 / span 5; // idk why 3 doesn't work
    }
`


const ClubsSection = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 48px;

    justify-items: start;
    align-items: start;

    width: 100%;
`

const Extra = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 48px;

    justify-items: start;
    align-items: start;

    width: 100%;

    padding: 10px 16px 24px 16px;

    width: 100%;

    background: ${p => p.theme.colors.bg};
    border-radius: 10px;

    ${up('tablet')} {
        margin: 0;
        padding: 10px 48px 40px 48px;
    }
`

export default ProfileInfo